import { Box, Button, debounce, Typography, Paper } from '@mui/material';
import Chip from '@mui/material/Chip';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import CustomInputField from 'src/components/CustomInputField';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import useSearchParams from 'src/hooks/useSearchParams';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import CollectionService from 'src/services/CollectionService';
import ProjectsService from 'src/services/ProjectsService';
import useCollectionsStore from 'src/stores/collections';
import useProjectsStore from 'src/stores/projects';
import useUserStore from 'src/stores/user';
import TelegramLogo from 'src/icons/TelegramLogo';
import { AIIcon } from 'src/icons/AIIcon';
import { CaseIcon } from 'src/icons/CaseIcon';
import CollectionsFilter from 'src/widgets/CollectionsFilter';
import CollectionsList from 'src/widgets/CollectionsList';
import TitleWithCounter from 'src/widgets/TitleWithCounter';
import Title from 'src/components/Title';

import { getBeginWorkStepByOrder } from 'src/utils/beginWork';

import type { IBeginWorkOnSuccessArgs } from 'src/types';
import type { IProjectBundle } from 'src/types/Projects';

interface ICollectionsFormValues {
    collections: string[];
}

export interface ICollectionProps {
    onSuccess?: ({ skiped }: IBeginWorkOnSuccessArgs) => void;
    /** Флаг онбординга - если true, не показываем ошибки при отсутствии проекта */
    isOnboarding?: boolean;
}

const ManagerBlock = () => {
    const handleContactManager = () => {
        window.open('mailto:support@leadsmania.ru', '_blank');
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                background: 'var(--lm-surface)',
                border: '1px solid var(--lm-border)',
                boxShadow: 'none',
            }}
        >
            <Typography
                variant="h5"
                fontWeight={700}
                color="text.primary"
                sx={{ fontSize: 20, mb: 1 }}
            >
                Не нашли нужную подборку?
            </Typography>
            <Typography
                variant="body2"
                mb={2}
                sx={{ color: 'text.primary' }}
            >
                Обратитесь к нашему менеджеру в Telegram. Мы поможем найти подходящие чаты для вашего бизнеса.
            </Typography>
            <Button
                variant="outlined"
                startIcon={<TelegramLogo color="#0088CC" />}
                onClick={handleContactManager}
                fullWidth
                size="large"
                sx={{
                    borderColor: '#0088CC',
                    color: '#0088CC',
                    borderRadius: '16px',
                    backgroundColor: 'rgba(0, 136, 204, 0.08)',
                    '&:hover': {
                        borderColor: '#0088CC',
                        backgroundColor: 'var(--lm-hover)',
                    },
                }}
            >
                @leadsmania_manager
            </Button>
        </Paper>
    );
};

export default function Collection({ onSuccess, isOnboarding = false }: ICollectionProps) {
    const { enqueueSnackbar } = useSnackbar();
    const routerLocation = useLocation();
    const isMobile = useIsMobileSize();
    const { cities, privateCollection, getPrivateCollection } = useCollectionsStore(
        ({ cities, privateCollection, getPrivateCollection }) => ({
            cities,
            privateCollection,
            getPrivateCollection,
        })
    );

    const { onboardingStep } = useUserStore(({ onboardingStep }) => ({
        onboardingStep,
    }));

    const { activeProject, setActiveProjectById, setActiveProject, projects, setProjects } = useProjectsStore(
        ({ activeProject, setActiveProjectById, setActiveProject, projects, setProjects }) => ({
            activeProject,
            setActiveProjectById,
            setActiveProject,
            projects,
            setProjects,
        })
    );

    const {
        params: { location = '', search = '' },
        setParams,
    } = useSearchParams();

    // Получаем projectId из URL
    const projectIdFromUrl = useMemo(() => {
        const urlSearchParams = new URLSearchParams(routerLocation.search);
        const param = urlSearchParams.get('projectId');
        return param ? parseInt(param, 10) : null;
    }, [routerLocation.search]);

    // Флаг - есть ли projectId в URL
    const hasProjectId = projectIdFromUrl !== null && !isNaN(projectIdFromUrl);

    // Загружаем проекты и устанавливаем активный проект
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);

    useEffect(() => {
        const loadProjects = async () => {
            // Если projectId из URL и он уже активен - ничего не делаем
            if (hasProjectId && activeProject?.id === projectIdFromUrl) {
                return;
            }

            // Если projectId из URL, но проект другой или не загружен
            if (hasProjectId) {
                const existingProject = projects.find((p) => p.id === projectIdFromUrl);
                if (existingProject) {
                    setActiveProjectById(projectIdFromUrl!);
                    return;
                }

                // Загружаем проекты
                setIsLoadingProjects(true);
                try {
                    const data = await ProjectsService.getProjects();
                    setProjects(data);
                    const targetProject = data.find((p) => p.id === projectIdFromUrl);
                    if (targetProject) {
                        setActiveProject(targetProject);
                    } else {
                        enqueueSnackbar('Проект не найден', { variant: 'error' });
                    }
                } catch {
                    enqueueSnackbar('Ошибка загрузки проектов', { variant: 'error' });
                } finally {
                    setIsLoadingProjects(false);
                }
                return;
            }

            // Онбординг режим - нет projectId в URL
            if (activeProject?.id) {
                return;
            }

            if (projects.length > 0) {
                setActiveProject(projects[0]);
                return;
            }

            // Загружаем проекты
            setIsLoadingProjects(true);
            try {
                const data = await ProjectsService.getProjects();
                setProjects(data);
                if (data.length > 0) {
                    setActiveProject(data[0]);
                }
            } catch {
                if (!isOnboarding) {
                    enqueueSnackbar('Ошибка загрузки проектов', { variant: 'error' });
                }
            } finally {
                setIsLoadingProjects(false);
            }
        };

        loadProjects();
    }, [
        hasProjectId,
        projectIdFromUrl,
        activeProject?.id,
        projects,
        setActiveProjectById,
        setActiveProject,
        setProjects,
        isOnboarding,
        enqueueSnackbar,
    ]);

    const methods = useForm<ICollectionsFormValues>({
        defaultValues: {
            collections: [],
        },
    });

    // Функция для клиентского поиска по названию подборки
    const filterCollectionsByName = useCallback((collections: any[], searchTerm: string) => {
        if (!searchTerm.trim()) return collections;

        const searchLower = searchTerm.toLowerCase();
        return collections.filter(
            (collection) =>
                collection.name.toLowerCase().includes(searchLower) ||
                collection.tags?.some((tag: string) => tag.toLowerCase().includes(searchLower))
        );
    }, []);

    const { watch, setValue } = methods;
    const selectedCollections = watch('collections');

    // Получаем лимит подборок из тарифа
    const bundlesLimit = useUserStore(({ current_subscription }) => current_subscription?.plan.bundles_limit) || 0;

    // Собираем подборки в зависимости от наличия projectId в URL
    // Если есть projectId - показываем только подборки активного проекта
    // Если нет projectId - показываем все уникальные подборки из всех проектов
    const allSelectedBundlesFromProjects = useMemo(() => {
        if (hasProjectId && activeProject) {
            // Режим конкретного проекта - показываем только его подборки
            if ('bundles' in activeProject && activeProject.bundles) {
                return activeProject.bundles.filter((bundle) => bundle.is_public);
            }
            return [];
        } else {
            // Режим всех проектов - собираем все уникальные подборки
            const bundlesMap = new Map<number, IProjectBundle>();
            projects.forEach((project) => {
                if ('bundles' in project && project.bundles) {
                    project.bundles.forEach((bundle) => {
                        if (bundle.is_public) {
                            bundlesMap.set(bundle.id, bundle);
                        }
                    });
                }
            });
            return Array.from(bundlesMap.values());
        }
    }, [projects, hasProjectId, activeProject]);

    // Подсчитываем общее количество уникальных публичных подборок из ВСЕХ проектов
    // (используется для отображения общего лимита)
    const totalBundlesCountAllProjects = useMemo(() => {
        const bundlesMap = new Map<number, IProjectBundle>();
        projects.forEach((project) => {
            if ('bundles' in project && project.bundles) {
                project.bundles.forEach((bundle) => {
                    if (bundle.is_public) {
                        bundlesMap.set(bundle.id, bundle);
                    }
                });
            }
        });
        return bundlesMap.size;
    }, [projects]);

    // Подсчитываем общее количество уникальных публичных подборок
    const totalBundlesCount = useMemo(() => {
        if (hasProjectId) {
            // Для конкретного проекта считаем только его подборки
            return allSelectedBundlesFromProjects.length;
        } else {
            // Для всех проектов считаем уникальные подборки
            return allSelectedBundlesFromProjects.length;
        }
    }, [allSelectedBundlesFromProjects, hasProjectId]);

    // Количество публичных подборок в текущем проекте (в форме)
    const currentProjectBundlesCount = selectedCollections.length;

    // Количество публичных подборок в текущем проекте (из сохраненных данных)
    const savedCurrentProjectBundlesCount = useMemo(() => {
        if (!activeProject || !('bundles' in activeProject)) return 0;
        const publicBundles = activeProject.bundles.filter((bundle) => bundle.is_public);
        return publicBundles.length;
    }, [activeProject]);

    // Общее количество публичных подборок с учетом текущего проекта
    // При наличии projectId показываем общий лимит по всем проектам
    const totalBundlesWithCurrent = useMemo(() => {
        if (hasProjectId) {
            // Для конкретного проекта: показываем общий лимит по всем проектам
            // (т.к. лимит общий для всех проектов)
            return totalBundlesCountAllProjects;
        } else {
            // Для всех проектов: считаем все уникальные подборки из всех проектов
            // (уже учтены в totalBundlesCount, так как allSelectedBundlesFromProjects содержит все уникальные)
            return totalBundlesCount;
        }
    }, [hasProjectId, currentProjectBundlesCount, totalBundlesCount, totalBundlesCountAllProjects]);

    // Считаем только публичные подборки (не учитываем личную подборку в лимите)
    const currentCount = selectedCollections.length;

    const hasSkipAbility = getBeginWorkStepByOrder(onboardingStep)?.id === 'choose_collections';

    const isMaxCount = bundlesLimit > 0 && totalBundlesWithCurrent >= bundlesLimit;

    const {
        data: { chat_bundles: publicCollections = [], cities: apiCities = [], locations: apiLocations = [] } = {},
        isLoading: isGetCollectionsLoading,
        isError: isGetCollectionsError,
    } = useQuery({
        queryKey: [CollectionService.queryKey, location],
        queryFn: () => CollectionService.getPublicCollections(location, []),
        onError: () => {
            enqueueSnackbar('Ошибка получения подборок', { variant: 'error' });
        },
    });

    // Рекомендованные подборки теперь получаются из activeProject.recommended_collections

    // Обновляем города в store при получении данных
    useEffect(() => {
        const fromApi = apiCities && apiCities.length > 0 ? apiCities : apiLocations;
        if (fromApi && fromApi.length > 0 && (!cities || cities.length === 0)) {
            useCollectionsStore.getState().setCollections({
                chat_bundles: [],
                cities: fromApi,
                locations: fromApi,
            });
        }
    }, [apiCities, apiLocations, cities]);

    const [showOnlySelected, setShowOnlySelected] = useState(false);
    const [showOnlyAIRecommended, setShowOnlyAIRecommended] = useState(false);

    // Получаем рекомендованные подборки из активного проекта
    const recommendedCollectionIds = activeProject?.recommended_collections || [];

    const selectedCollectionObjs = useMemo(() => {
        // Показываем только выбранные публичные подборки
        if (!publicCollections || !selectedCollections) return [];
        return publicCollections.filter((col) => selectedCollections.includes(String(col.id)));
    }, [publicCollections, selectedCollections]);

    // Применяем фильтрацию по названию и тегам (только публичные подборки)
    const filteredCollections = useMemo(() => {
        if (!publicCollections) return [];
        let base = publicCollections;
        if (search && search.trim()) {
            base = filterCollectionsByName(publicCollections, search);
        }
        if (showOnlyAIRecommended && recommendedCollectionIds.length > 0) {
            base = base.filter((col) => recommendedCollectionIds.includes(col.id));
        }
        if (showOnlySelected && selectedCollections) {
            base = base.filter((col) => selectedCollections.includes(String(col.id)));
        }
        return base;
    }, [
        publicCollections,
        search,
        filterCollectionsByName,
        showOnlySelected,
        showOnlyAIRecommended,
        selectedCollections,
        recommendedCollectionIds,
    ]);

    useEffect(() => {
        // Загружаем privateCollection при инициализации
        if (activeProject?.id && !privateCollection?.id) {
            getPrivateCollection().catch(() => {
                // Ошибка загрузки личной подборки
            });
        }
    }, [activeProject?.id, privateCollection?.id, getPrivateCollection]);

    // Загружаем города при инициализации, если их нет
    useEffect(() => {
        if (!cities || cities.length === 0) {
            // Города загружаются вместе с подборками через API
            // Дополнительная загрузка не требуется
        }
    }, [cities]);

    // Отслеживаем предыдущие значения для определения смены проекта
    const prevProjectIdRef = useRef<number | undefined>(undefined);
    const prevHasProjectIdRef = useRef<boolean>(false);

    useEffect(() => {
        // Инициализируем форму выбранными подборками
        // Если есть projectId - показываем подборки конкретного проекта
        // Если нет projectId - показываем все подборки из всех проектов
        const projectChanged =
            prevProjectIdRef.current !== activeProject?.id || prevHasProjectIdRef.current !== hasProjectId;

        if (
            projectChanged ||
            (selectedCollections && !selectedCollections.length && allSelectedBundlesFromProjects.length > 0)
        ) {
            const defaultValues = allSelectedBundlesFromProjects.map((bundle) => String(bundle.id));
            setValue('collections', defaultValues);

            prevProjectIdRef.current = activeProject?.id;
            prevHasProjectIdRef.current = hasProjectId;
        }
        // eslint-disable-next-line
    }, [projects, hasProjectId, activeProject?.id, allSelectedBundlesFromProjects]);

    // Убрана автоматическая проверка лимита - показываем все выбранные подборки
    // Лимит проверяется только при сохранении

    const { mutate: updateProject, isLoading: isupdateProjectLoading } = useMutation(
        useProjectsStore.getState().updateProject,
        {
            onSuccess: () => {
                onSuccess?.({ skiped: false });
                // Обновляем проект после сохранения, чтобы получить актуальные данные
                if (activeProject?.id) {
                    ProjectsService.getProjects().then((data) => {
                        useProjectsStore.getState().setProjects(data);
                        useProjectsStore.getState().setActiveProjectById(activeProject.id);
                    });
                }
                enqueueSnackbar('Подборки сохранены', { variant: 'success' });
            },

            onError: (_error) => {
                enqueueSnackbar('Ошибка сохранения подборок', { variant: 'error' });
            },
        }
    );

    const onSubmit = ({ collections: collectionsInput }: ICollectionsFormValues) => {
        if (hasSkipAbility && !currentCount) {
            onSuccess?.({ skiped: true });
            return;
        }

        // Проверяем что collections существует и является массивом
        if (!collectionsInput || !Array.isArray(collectionsInput)) {
            enqueueSnackbar('Ошибка: не выбраны подборки', { variant: 'error' });
            return;
        }

        // Проверяем общий лимит перед сохранением
        // Но разрешаем сохранение, если пользователь уменьшает количество подборок
        const newCurrentCount = collectionsInput.length;
        const isReducing = newCurrentCount <= savedCurrentProjectBundlesCount;

        // Собираем все уникальные подборки из всех проектов (кроме текущего)
        const existingBundlesSet = new Set<number>();
        projects.forEach((project) => {
            if (project.id !== activeProject?.id && 'bundles' in project && project.bundles) {
                project.bundles.forEach((bundle) => {
                    if (bundle.is_public) {
                        existingBundlesSet.add(bundle.id);
                    }
                });
            }
        });

        // Добавляем новые выбранные подборки
        collectionsInput.forEach((id) => {
            existingBundlesSet.add(Number(id));
        });

        const totalWithNew = existingBundlesSet.size;

        // Если превышен лимит и пользователь НЕ уменьшает количество — ограничиваем
        let collectionsToSave = collectionsInput;
        if (bundlesLimit > 0 && totalWithNew > bundlesLimit && !isReducing) {
            // Учитываем уникальность - некоторые подборки могут быть в нескольких проектах
            const uniqueOtherBundles = new Set<number>();
            projects.forEach((project) => {
                if (project.id !== activeProject?.id && 'bundles' in project && project.bundles) {
                    project.bundles.forEach((bundle) => {
                        if (bundle.is_public) {
                            uniqueOtherBundles.add(bundle.id);
                        }
                    });
                }
            });

            const available = bundlesLimit - uniqueOtherBundles.size;
            if (available > 0) {
                collectionsToSave = collectionsInput.slice(0, available);
            } else {
                collectionsToSave = [];
            }

            enqueueSnackbar(
                `Превышен лимит подборок (${bundlesLimit}). Сохранено ${collectionsToSave.length} подборок.`,
                {
                    variant: 'warning',
                }
            );
        }

        // Если личная подборка не загружена, загружаем её
        if (!privateCollection?.id) {
            getPrivateCollection()
                .then(() => {
                    // После загрузки повторяем отправку
                    const updatedPrivateCollection = useCollectionsStore.getState().privateCollection;
                    if (updatedPrivateCollection?.id) {
                        // Проверяем что activeProject существует
                        if (!activeProject?.id) {
                            enqueueSnackbar('Ошибка: проект не найден', { variant: 'error' });
                            return;
                        }

                        updateProject({
                            id: activeProject.id,
                            bundles: [updatedPrivateCollection.id, ...collectionsToSave.map((id) => Number(id))],
                        });
                    } else {
                        enqueueSnackbar('Не удалось загрузить личную подборку. Попробуйте обновить страницу.', {
                            variant: 'error',
                        });
                    }
                })
                .catch(() => {
                    enqueueSnackbar('Ошибка загрузки личной подборки. Попробуйте обновить страницу.', {
                        variant: 'error',
                    });
                });
            return;
        }

        // Проверяем что activeProject существует
        if (!activeProject?.id) {
            enqueueSnackbar('Ошибка: проект не найден', { variant: 'error' });
            return;
        }

        updateProject({
            id: activeProject.id,
            bundles: [privateCollection.id, ...collectionsToSave.map((id) => Number(id))],
        });
    };

    const [searchValue, setSearchValue] = useState(search);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const onSearchChange = (value: string) => {
        setSearchValue(value);
        debouncedSearchChange(value);
    };

    // Автофокус на инпут после получения результатов
    useEffect(() => {
        if (!isGetCollectionsLoading && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isGetCollectionsLoading]);

    const debouncedSearchChange = useCallback(
        debounce((value: string) => {
            onFilteredCitiesChange(value);
        }, 800),
        []
    );

    const onFilterTagsChangeHandler = (value: string) => {
        setParams({ search: value });
    };

    const onFilteredCitiesChange = useCallback(onFilterTagsChangeHandler, []);

    if (isGetCollectionsError) return null;

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={methods.handleSubmit(onSubmit)}
                style={{ minHeight: '100%', display: 'grid' }}
            >
                <Box
                    display="flex"
                    minHeight="100%"
                    flexDirection="column"
                    pt={2}
                >
                    <LoadingWrapper
                        isLoading={isGetCollectionsLoading || isupdateProjectLoading || isLoadingProjects}
                        isForced
                    >
                        <Box
                            mb={1.5}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                        >
                            <Title>Где искать?</Title>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                {hasProjectId && activeProject?.name && (
                                    <Chip
                                        icon={<CaseIcon />}
                                        label={activeProject.name}
                                        size="medium"
                                        sx={{
                                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                            color: 'text.primary',
                                            fontWeight: 600,
                                            border: '1px solid rgba(25, 118, 210, 0.2)',
                                            paddingLeft: '8px',
                                            paddingRight: '8px',
                                            gap: '8px',
                                            '& .MuiChip-icon': {
                                                color: 'text.primary',
                                                marginLeft: 0,
                                                marginRight: 0,
                                            },
                                        }}
                                    />
                                )}
                                {bundlesLimit > 0 && (
                                    <Chip
                                        color="primary"
                                        variant={totalBundlesWithCurrent >= bundlesLimit ? 'filled' : 'outlined'}
                                        label={`${totalBundlesWithCurrent} из ${bundlesLimit}`}
                                        sx={{
                                            background:
                                                totalBundlesWithCurrent < bundlesLimit && !isMobile
                                                    ? 'var(--lm-surface)'
                                                    : null,
                                            minWidth: '80px',
                                            justifyContent: 'center',
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                        <Box
                            maxWidth="80%"
                            mb={20 / 8}
                        >
                            <Typography
                                variant="body1"
                                color="text.primary"
                            >
                                Выберите готовые подборки тематических чатов, в которых могут находиться ваши
                                потенциальные клиенты
                            </Typography>
                        </Box>
                        <Box mb={1.5}>
                            <CustomInputField
                                ref={searchInputRef}
                                placeholder="Искать по тематике или услуге"
                                value={searchValue}
                                onChange={onSearchChange}
                                name="search"
                                disabled={false}
                                icon="search"
                            />
                        </Box>
                        <Box
                            mb={2}
                            display="flex"
                            gap={1}
                            alignItems="center"
                            flexWrap="wrap"
                        >
                            <CollectionsFilter
                                cities={cities || []}
                                isDisabled={false}
                            />
                            <Chip
                                icon={
                                    <AIIcon
                                        width={16}
                                        height={16}
                                    />
                                }
                                label="ИИ рекомендует"
                                color={showOnlyAIRecommended ? 'primary' : 'default'}
                                variant={showOnlyAIRecommended ? 'filled' : 'outlined'}
                                onClick={() => setShowOnlyAIRecommended((v) => !v)}
                                clickable
                                disabled={recommendedCollectionIds.length === 0}
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 15,
                                    backgroundColor: showOnlyAIRecommended ? undefined : 'var(--lm-surface)',
                                    borderColor: showOnlyAIRecommended ? undefined : 'rgba(122, 48, 228, 0.3)',
                                    color: showOnlyAIRecommended ? undefined : '#7A30E4',
                                    '&:hover': {
                                        backgroundColor: showOnlyAIRecommended ? undefined : 'rgba(122, 48, 228, 0.05)',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.5,
                                        cursor: 'not-allowed',
                                    },
                                    '& .MuiChip-icon': {
                                        color: showOnlyAIRecommended ? 'var(--lm-accent-contrast)' : '#7A30E4',
                                    },
                                }}
                            />
                            <Chip
                                label="Мои подборки"
                                color={showOnlySelected ? 'primary' : 'default'}
                                variant={showOnlySelected ? 'filled' : 'outlined'}
                                onClick={() => setShowOnlySelected((v) => !v)}
                                clickable
                                disabled={selectedCollectionObjs.length === 0}
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 15,
                                    backgroundColor: showOnlySelected ? undefined : 'var(--lm-surface)',
                                    '&:hover': {
                                        backgroundColor: showOnlySelected ? undefined : 'var(--lm-canvas)',
                                    },
                                    '&.Mui-disabled': {
                                        opacity: 0.5,
                                        cursor: 'not-allowed',
                                    },
                                }}
                            />
                        </Box>
                        <Box mb={1.5}>
                            <CollectionsList
                                list={filteredCollections}
                                isDisabled={false}
                                recommendedIds={recommendedCollectionIds}
                            />
                        </Box>
                        {isMobile && <ManagerBlock />}
                        <Box
                            display="flex"
                            alignItems="flex-end"
                            flexGrow={1}
                            mb={1}
                            sx={{
                                position: isMobile || currentCount > 0 ? 'sticky' : 'static',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: isMobile ? 'white' : 'transparent',
                                padding: isMobile ? '16px' : currentCount > 0 ? '16px' : '16px 16px 32px 16px',
                                paddingBottom: isMobile ? '16px' : '32px',
                                zIndex: 10,
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                fullWidth
                                type="submit"
                                sx={{
                                    minHeight: '48px',
                                }}
                            >
                                {hasSkipAbility ? (currentCount ? 'Продолжить' : 'Пропустить') : 'Сохранить'}
                            </Button>
                        </Box>
                    </LoadingWrapper>
                </Box>
            </form>
        </FormProvider>
    );
}
